import { queryClient } from '@/query/queryClient';
import { SESSION_ROOT } from '@/query/keys';
import { clearActiveAssignment } from '@/auth/activeAssignment';
import {
  type ResetMode,
  removeLegacyAccessKeys,
  removeLegacySelectionKeys,
  resetRoleState,
} from '@/auth/roleState';
import { cancelHeaderSelectionSave } from '@/selection/headerSelection';
import { clearAccessTokenCopy } from '@/utils/accessTokenCopy';

/**
 * The one function that ends or changes a session (UI state plan, phase 0):
 * sign-out, session expiry, role switch and "no access" all go through it.
 * It clears, in this order:
 *
 * 1. the TanStack Query cache (every role's data, incl. the header selection
 *    of the role: brand, partner, filter; on sign-out the session too). A
 *    change of the selection still waiting to be saved is dropped;
 * 2. the in-memory session (user, active assignment, permissions, modules);
 * 3. the role-dependent browser storage (auth/roleState.ts keep-list) and the
 *    access keys older builds left in localStorage.
 *
 * Modes:
 * - `switch`:    role switch (and a role preview starting or ending): the
 *                user stays signed in; everything loaded for the previous
 *                role is dropped. The caller sets the new assignment and
 *                loads its data.
 * - `no_access`: the signed-in user has no valid role: as `switch`, and no
 *                assignment stays active (the no-access page only offers
 *                "Sign out").
 * - `signout`:   logout: the whole session and cache.
 * - `expired`:   session expired (401) or a failed SSO callback: as
 *                `signout`, but `sessionurl` is kept so the next sign-in
 *                returns to the page.
 *
 * It does not sign out of Cognito (ApiProvider.logoutUserLocally does) and
 * does not navigate. Returns the removed storage keys (`local:<key>` /
 * `session:<key>`), for tests and debugging.
 */
export type ClearMode = ResetMode | 'no_access';

export function clearSession(mode: ClearMode = 'signout'): string[] {
  const storageMode: ResetMode = mode === 'no_access' ? 'switch' : mode;

  // the header selection goes with the cache below; nothing of it is sent any more
  cancelHeaderSelectionSave();

  if (storageMode === 'switch') {
    // everything loaded for a role (`['a', <assignment id>, ...]` and any other data);
    // who is signed in, their list of roles and the active assignment stay (the caller
    // of a real switch sets the new assignment next)
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== SESSION_ROOT });
  } else {
    queryClient.clear();
    clearAccessTokenCopy();
  }

  const removed = resetRoleState(storageMode);
  removeLegacyAccessKeys();
  removeLegacySelectionKeys();
  if (mode !== 'switch') clearActiveAssignment();
  return removed;
}
